import React, { createContext, useContext, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const AssistantContext = createContext();

export function AssistantProvider({ children }) {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [conversationId, setConversationId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const { user, token } = useAuth();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  // Ouvrir/fermer le chat
  const toggleChat = useCallback(() => {
    setIsOpen(prev => !prev);
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Envoyer un message
  const sendMessage = useCallback(async (message) => {
    if (!message.trim() || !user || !token) {
      console.error('Cannot send message: missing user or token');
      return;
    }

    // Ajouter message utilisateur immédiatement
    const userMessage = {
      role: 'user',
      content: message,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Contexte de page
      const context = {
        page: window.location.pathname,
        timestamp: new Date().toISOString()
      };

      const response = await axios.post(
        `${backendUrl}/api/ai-assistant/chat`,
        {
          message,
          context,
          conversation_id: conversationId
        },
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      // Ajouter réponse assistant
      const assistantMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, assistantMessage]);

      // Mettre à jour conversation_id
      if (response.data.conversation_id) {
        setConversationId(response.data.conversation_id);
      }

      // Mettre à jour suggestions
      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
      }

    } catch (error) {
      console.error('Erreur assistant:', error);
      console.error('Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      
      // Message d'erreur fallback
      const errorMessage = {
        role: 'assistant',
        content: "Désolé, je rencontre un problème technique. Vous pouvez consulter notre FAQ ou contacter contact@nannyou.fr pour une assistance immédiate.",
        timestamp: new Date().toISOString(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [user, conversationId, backendUrl, token]);

  // Charger suggestions contextuelles
  const loadSuggestions = useCallback(async () => {
    if (!user) return;

    try {
      const response = await axios.get(
        `${backendUrl}/api/ai-assistant/suggestions`,
        {
          params: { page: window.location.pathname },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.suggestions) {
        setSuggestions(response.data.suggestions);
      }
    } catch (error) {
      console.error('Erreur chargement suggestions:', error);
    }
  }, [user, backendUrl, token]);

  // Réinitialiser conversation
  const resetConversation = useCallback(() => {
    setMessages([]);
    setConversationId(null);
    setSuggestions([]);
  }, []);

  // Supprimer conversation
  const deleteConversation = useCallback(async () => {
    if (!conversationId || !user) return;

    try {
      await axios.delete(
        `${backendUrl}/api/ai-assistant/history/${conversationId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      resetConversation();
    } catch (error) {
      console.error('Erreur suppression conversation:', error);
    }
  }, [user, conversationId, backendUrl, token, resetConversation]);

  const value = {
    isOpen,
    messages,
    isLoading,
    suggestions,
    conversationId,
    toggleChat,
    openChat,
    closeChat,
    sendMessage,
    loadSuggestions,
    resetConversation,
    deleteConversation
  };

  return (
    <AssistantContext.Provider value={value}>
      {children}
    </AssistantContext.Provider>
  );
}

export function useAssistant() {
  const context = useContext(AssistantContext);
  if (!context) {
    throw new Error('useAssistant must be used within AssistantProvider');
  }
  return context;
}
